import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';

const Login = () => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const [tenantName, setTenantName] = useState('');
	const navigate = useNavigate();
	const { login } = useAuth();

	// Detect tenant from subdomain
	React.useEffect(() => {
		const hostname = window.location.hostname;
		const parts = hostname.split('.');
		// Check for subdomain (e.g., tenant.localhost or tenant.domain.com)
		// Localhost with subdomain has 2 parts (tenant.localhost) if viewed as such, but strictly localhost is valid TLD in some contexts? 
		// Actually typically localhost is just localhost. tenant.localhost is 2 parts.
		// domain.com is 2 parts. tenant.domain.com is 3 parts.
		if ((parts.length > 2) || (parts.length === 2 && parts[1] === 'localhost')) {
			const subdomain = parts[0];
			if (subdomain !== 'www' && subdomain !== 'app' && subdomain !== 'localhost') {
				setTenantName(subdomain.charAt(0).toUpperCase() + subdomain.slice(1));
			}
		}
	}, []);

	const handleLogin = async (e) => {
		e.preventDefault();
		setError('');
		setLoading(true);

		try {
			// Extract tenant from URL path
			const pathParts = window.location.pathname.split('/').filter(p => p); // Remove empty strings
			// If first segment is 'login' or 'signup', it's not a tenant - use 'default'
			const firstSegment = pathParts[0];
			const tenantIdentifier = (firstSegment && firstSegment !== 'login' && firstSegment !== 'signup')
				? firstSegment
				: 'default';

			console.log('Login - Current URL:', window.location.pathname);
			console.log('Login - Path parts:', pathParts);
			console.log('Login - Tenant identifier:', tenantIdentifier);

			const response = await authAPI.login(email, password, tenantIdentifier);
			console.log('Login - Response received:', response);

			login(response);
			console.log('Login - Auth context updated');

			const tenantPath = response.tenantIdentifier || 'default';
			console.log('Login - Tenant path:', tenantPath);
			console.log('Login - User role:', response.role);

			// Redirect based on role
			if (response.role === 'ADMIN' || response.role === 'TENANT_ADMIN') {
				console.log('Login - Navigating to dashboard:', `/${tenantPath}/dashboard`);
				navigate(`/${tenantPath}/dashboard`);
			} else if (response.role === 'STARTUP') {
				console.log('Login - Navigating to startup dashboard');
				navigate(`/${tenantPath}/startup/dashboard`);
			} else if (response.role === 'INVESTOR') {
				console.log('Login - Navigating to investor dashboard');
				navigate(`/${tenantPath}/investor/dashboard`);
			} else if (response.role === 'INVESTOR_NETWORK') {
				console.log('Login - Navigating to investor network dashboard');
				navigate(`/${tenantPath}/investor-network/dashboard`);
			} else if (response.role === 'EVENT_ORGANIZER') {
				console.log('Login - Navigating to organizer dashboard');
				navigate(`/${tenantPath}/organizer/dashboard`);
			} else if (response.role === 'MANAGEMENT') {
				console.log('Login - Navigating to management dashboard');
				navigate(`/${tenantPath}/user/dashboard`);
			} else if (response.role === 'VIEWER') {
				console.log('Login - Navigating to viewer dashboard');
				navigate(`/${tenantPath}/viewer/dashboard`);
			}
		} catch (err) {
			console.error('Login - Error:', err);
			setError(err.response?.data?.error || err.response?.data?.message || 'Login failed. Please check your credentials.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-md w-full">
				<div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
					<div className="px-10 py-12">
						<div className="text-center mb-8">
							<div className="flex justify-center items-center mb-6">
								<Logo showText={true} width={80} height={80} className="justify-center" />
							</div>
							<h2 className="text-3xl font-bold text-gray-900 mb-2">
								Welcome Back
							</h2>
							<p className="text-gray-600 mb-1">
								Sign in to your account
							</p>
							<p className="text-sm text-gray-500">
								a product of Techpuram
							</p>
						</div>

						<form className="space-y-6" onSubmit={handleLogin}>
							{error && (
								<div className="bg-red-50 border border-red-200 rounded-lg p-4">
									<div className="flex items-center">
										<svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
											<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
										</svg>
										<span className="text-sm text-red-800">{error}</span>
									</div>
								</div>
							)}

							<div className="space-y-5">
								<div>
									<label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
										Email Address
									</label>
									<input
										id="email"
										name="email"
										type="email"
										required
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900 placeholder-gray-400"
										placeholder="Enter your email"
									/>
								</div>
								<div>
									<label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
										Password
									</label>
									<input
										id="password"
										name="password"
										type="password"
										required
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900 placeholder-gray-400"
										placeholder="Enter your password"
									/>
								</div>
							</div>

							<div className="pt-4">
								<button
									type="submit"
									disabled={loading}
									className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition duration-200 transform hover:scale-[1.02]"
								>
									{loading ? (
										<span className="flex items-center justify-center">
											<svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
												<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
											</svg>
											Signing in...
										</span>
									) : (
										'Sign In'
									)}
								</button>
							</div>
						</form>

					</div>
				</div>

				{/* Copyright Statement */}
				<div className="mt-6 text-center">
					<p className="text-xs text-gray-500">
						© {new Date().getFullYear()} Techpuram. All rights reserved.
					</p>
				</div>
			</div>
		</div>
	);
};

export default Login;


